import { resolveApiErrorMessage } from "@kontextwerk/control-web-client"

export type KehrblattApiError = {
  message: string
  statusCode?: number
  reasonCode?: string
  requestId?: string
  fieldErrors: unknown[]
  detail?: unknown
  raw: unknown
}

const DEFAULT_FALLBACK = "Die Aktion konnte nicht ausgeführt werden."

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const textValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined

  const text = String(value).trim()

  return text || undefined
}

const normalizeReasonCode = (value: unknown): string | undefined => {
  const text = textValue(value)

  return text ? text.replace(/[-\s]+/g, "_").toLowerCase() : undefined
}

const readReasonCode = (error: unknown): string | undefined => {
  if (!isRecord(error)) return undefined

  const nestedError = isRecord(error.error) ? error.error : null

  return (
    normalizeReasonCode(error.reasonCode) ??
    normalizeReasonCode(error.reason_code) ??
    normalizeReasonCode(error.code) ??
    (nestedError
      ? normalizeReasonCode(nestedError.reasonCode ?? nestedError.reason_code ?? nestedError.code)
      : undefined)
  )
}

const readRequestId = (error: unknown): string | undefined => {
  if (!isRecord(error)) return undefined

  const nestedError = isRecord(error.error) ? error.error : null

  return (
    textValue(error.requestId) ??
    textValue(error.request_id) ??
    (nestedError ? textValue(nestedError.requestId ?? nestedError.request_id) : undefined)
  )
}

const readFieldErrors = (error: unknown): unknown[] => {
  if (!isRecord(error)) return []

  const value = error.fieldErrors ?? error.field_errors

  return Array.isArray(value) ? value : []
}

export const getApiError = (error: unknown, fallback = DEFAULT_FALLBACK): KehrblattApiError => {
  const record = isRecord(error) ? error : null
  const detail = record?.detail ?? record?.payload ?? (error instanceof Error ? null : error)
  const fieldErrors = readFieldErrors(error)
  const statusCode =
    typeof record?.statusCode === "number"
      ? record.statusCode
      : typeof record?.status === "number"
        ? record.status
        : undefined

  return {
    message: resolveApiErrorMessage(fieldErrors.length ? fieldErrors : detail, {
      statusCode,
      fallback,
    }),
    statusCode,
    reasonCode: readReasonCode(error),
    requestId: readRequestId(error),
    fieldErrors,
    detail,
    raw: error,
  }
}

export const getApiErrorMessage = (error: unknown, fallback = DEFAULT_FALLBACK): string =>
  getApiError(error, fallback).message
